<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    ConfirmationModal,
    Spinner,
  },
  props: {
    conversationId: {
      type: Number,
      default: null,
    },
    conversationDisplayId: {
      type: Number,
      default: null,
    },
    showBroadcasts: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['edit'],
  computed: {
    ...mapGetters({
      uiFlags: 'scheduledMessages/getUIFlags',
    }),
    scheduledMessages() {
      if (this.showBroadcasts) {
        return this.$store.getters['scheduledMessages/getBroadcastMessages'] || [];
      }
      if (this.conversationId) {
        return (
          this.$store.getters[
            'scheduledMessages/getScheduledMessagesForConversation'
          ](this.conversationId) || []
        );
      }
      return [];
    },
    sortedMessages() {
      return [...this.scheduledMessages].sort((a, b) => {
        return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      });
    },
    isLoading() {
      return this.uiFlags.isFetching;
    },
  },
  mounted() {
    this.fetchMessages();
  },
  methods: {
    fetchMessages() {
      const apiConversationId = this.conversationDisplayId || this.conversationId;
      if (apiConversationId) {
        this.$store.dispatch(
          'scheduledMessages/fetchForConversation',
          {
            conversationId: apiConversationId,
            scheduleableId: this.conversationId || apiConversationId,
          }
        );
      } else if (this.showBroadcasts) {
        this.$store.dispatch('scheduledMessages/fetchBroadcasts');
      }
    },
    formatDateTime(datetime, timezone) {
      if (!datetime) return '';
      const date = new Date(datetime);
      try {
        return new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
          timeZone: timezone || 'America/Sao_Paulo',
        }).format(date);
      } catch (error) {
        return new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(date);
      }
    },
    getRecurrenceText(type, config) {
      if (!type || type === 'no_recurrence') return 'Envio único';

      switch (type) {
        case 'daily':
          return 'Diário';
        case 'weekly': {
          const daysMap = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
          const days = (config.days_of_week || [])
            .map(d => daysMap[d])
            .join(', ');
          return `Semanal: ${days}`;
        }
        case 'monthly':
          return `Mensal (dia ${config.day_of_month || 1})`;
        case 'custom_interval':
          return `A cada ${config.interval_days || 1} dia(s)`;
        default:
          return 'Envio único';
      }
    },
    getStatusBadgeClasses(status) {
      switch (status) {
        case 'pending':
          return 'bg-n-slate-3 text-n-slate-11';
        case 'failed':
          return 'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6';
        case 'sent':
          return 'bg-n-grass-2 text-n-grass-11 border border-n-grass-6';
        default:
          return 'bg-n-slate-3 text-n-slate-11';
      }
    },
    getStatusText(status) {
      switch (status) {
        case 'pending':
          return 'Pendente';
        case 'failed':
          return 'Falhado';
        case 'sent':
          return 'Enviado';
        default:
          return status;
      }
    },
    async cancelMessage(msg) {
      // Show confirmation modal
      const confirmed = await this.$refs.confirmDeleteModal.showConfirmation();

      if (!confirmed) {
        return;
      }

      try {
        await this.$store.dispatch('scheduledMessages/delete', {
          conversationId: this.conversationDisplayId || this.conversationId,
          id: msg.id,
          isBroadcast: msg.target_scope === 'broadcast',
        });
        useAlert('Mensagem cancelada com sucesso');
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Erro ao cancelar mensagem';
        useAlert(`Erro ao cancelar: ${errorMessage}`);
      }
    },
    isWhatsappOfficial(msg) {
      return msg?.inbox?.channel_type === 'Channel::Whatsapp';
    },
    isChannelApi(msg) {
      return msg?.inbox?.channel_type === 'Channel::Api';
    },
    hasAttachment(msg) {
      return Array.isArray(msg?.attachment_ids) && msg.attachment_ids.length > 0;
    },
    getTemplateName(msg) {
      const attrs = msg?.additional_attributes || {};
      const templateParams =
        attrs.template_params || attrs.templateParams || {};
      return templateParams.name || '';
    },
    shouldShowTemplatePreview(msg) {
      return this.isWhatsappOfficial(msg) && this.getTemplateName(msg);
    },
    shouldShowMediaIcon(msg) {
      return this.isChannelApi(msg) && this.hasAttachment(msg);
    },
  },
};
</script>

<template>
  <div class="scheduled-messages-list">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner size="small" color-scheme="primary" />
      <span class="ml-2 text-n-slate-11">Carregando...</span>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedMessages.length === 0"
      class="empty-state flex flex-col items-center justify-center py-12"
    >
      <fluent-icon icon="mail-inbox" class="text-n-slate-9 mb-2" size="40" />
      <p class="text-n-slate-11 text-sm">
        {{ showBroadcasts ? 'Nenhum disparo agendado' : 'Nenhuma mensagem agendada' }}
      </p>
    </div>

    <!-- Messages List -->
    <div v-else class="space-y-2">
      <div
        v-for="msg in sortedMessages"
        :key="msg.id"
        class="flex items-start gap-3"
      >
        <!-- Card Content -->
        <div class="card bg-n-alpha-2 dark:bg-n-solid-1 border border-n-weak rounded-lg p-2.5 hover:border-n-iris-6 transition-all flex-1 min-w-0">
          <!-- Header with DateTime and Status -->
          <div class="flex items-center justify-between gap-2 mb-1.5">
            <!-- DateTime -->
            <div class="flex items-center gap-1.5 min-w-0">
              <fluent-icon icon="send-clock" class="text-n-iris-9 flex-shrink-0" size="14" />
              <span class="text-xs font-medium text-n-slate-12 truncate">
                {{ formatDateTime(msg.scheduled_at, msg.timezone) }}
              </span>
            </div>

            <!-- Status Badge -->
            <span
              :class="[
                'badge text-xs px-1.5 py-0.5 rounded flex-shrink-0',
                getStatusBadgeClasses(msg.status)
              ]"
            >
              {{ getStatusText(msg.status) }}
            </span>
          </div>

        <!-- Content Preview -->
        <div class="text-xs text-n-slate-11 mb-1.5 pl-6 flex items-center gap-1.5">
          <template v-if="shouldShowTemplatePreview(msg)">
            <span class="text-n-iris-11 font-medium">Template:</span>
            <span class="line-clamp-1">{{ getTemplateName(msg) }}</span>
          </template>
          <template v-else-if="shouldShowMediaIcon(msg)">
            <fluent-icon icon="attach" size="14" class="text-n-slate-9 flex-shrink-0" />
            <span class="line-clamp-1">Mídia anexada</span>
          </template>
          <template v-else>
            <span class="line-clamp-1">
              {{ msg.content || '(Template WhatsApp)' }}
            </span>
          </template>
        </div>

        <!-- Info Row -->
        <div class="flex items-center gap-3 flex-wrap pl-6">
          <!-- Tipo de Envio -->
          <div class="flex items-center gap-1 text-xs text-n-slate-10">
            <fluent-icon icon="send" class="flex-shrink-0" size="12" />
            <span class="truncate">{{ getRecurrenceText(msg.recurrence_type, msg.recurrence_config) }}</span>
          </div>

          <!-- Sender -->
          <div
            v-if="msg.sender"
            class="flex items-center gap-1 text-xs text-n-slate-10"
          >
            <fluent-icon icon="person" class="flex-shrink-0" size="12" />
            <span class="truncate">{{ msg.sender.name }}</span>
          </div>

          <!-- Attachments -->
          <div
            v-if="msg.attachment_ids && msg.attachment_ids.length > 0"
            class="flex items-center gap-1 text-xs text-n-slate-10"
          >
            <fluent-icon icon="attach" class="flex-shrink-0" size="12" />
            <span>{{ msg.attachment_ids.length }}</span>
          </div>

          <!-- Broadcast Badge -->
          <span
            v-if="msg.target_scope === 'broadcast'"
            class="badge bg-n-amber-2 text-n-amber-11 text-xs px-1.5 py-0.5 rounded flex items-center gap-1"
          >
            <fluent-icon icon="megaphone" size="12" />
            <span>Broadcast</span>
          </span>
        </div>

        <!-- Error Message -->
        <div
          v-if="msg.status === 'failed' && msg.error_message"
          class="mt-2 p-2 bg-n-ruby-2 border border-n-ruby-6 rounded text-xs text-n-ruby-11 flex items-start gap-1.5"
        >
          <fluent-icon icon="warning" class="mt-0.5 flex-shrink-0" size="12" />
          <span class="line-clamp-2">{{ msg.error_message }}</span>
        </div>
      </div>

      <!-- Action Buttons Column (Outside card) -->
      <div class="flex flex-col gap-2 flex-shrink-0">
        <button
          v-tooltip.left="'Cancelar'"
          class="flex items-center justify-center text-n-ruby-9 hover:text-n-ruby-11 transition-colors"
          @click="cancelMessage(msg)"
        >
          <fluent-icon icon="dismiss-circle" size="20" />
        </button>
      </div>
    </div>
  </div>
  </div>

  <!-- Confirmation Modal for Delete -->
  <ConfirmationModal
    ref="confirmDeleteModal"
    title="Cancelar Mensagem Agendada"
    description="Tem certeza que deseja cancelar esta mensagem agendada? Esta ação não pode ser desfeita."
    confirm-label="Sim, Cancelar"
    cancel-label="Não"
  />
</template>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
